import { DatasetInitialized, LimitExceeded } from "extractedBackendTypes";
import {
  SET_CONNECT_STATUS,
  SET_PREVIEW_DATA,
  SET_S3_FETCH_PROGRESS,
  SET_NEW_DATASET,
  SET_ACTIVITY_LOGS,
  RESET_STORE,
  SET_ANNOTATION_STATUS,
  SET_UPLOAD_STATUS,
  SET_IMAGE_ERROR_FILE_NAME,
  SET_ACTIVITY_OFFSET,
  SET_IS_FATAL_ERROR,
  SET_PREVIEW_DATA_SOURCE,
  SET_FILES_UPLOAD_TRANS_ID,
  SET_INDEXING_STATUS,
  SET_LIMIT_EXCEEDED_DETAILS,
  SET_ARE_ANNOTATIONS_FETCHED,
  SET_STATUS_CALL_IN_PROGRESS,
  SET_IS_LOADING,
  SET_SHOW_DELETE_DATASET_MODAL,
  SET_LAST_USED_DATA,
  SET_UPLOAD_FINISHED,
  SET_USER_INITIATED_CREATION,
  SET_FILES_UPLOAD_SIZE,
  SET_FILES_UPLOAD_PROGRESS,
  SET_SHOW_ACTIVITY_LOG_MODAL,
  SET_CVAT_DATA,
  SET_IS_CVAT_LOADING,
  // New Dataset Creation Workflow
  SET_FOLDERS_METADATA,
  SET_UPLOADING_FOLDER,
  SET_FOLDER_UPLOAD_STATUS,
  SET_MULTI_FOLDER_UPLOAD,
  UPDATE_MULTI_FOLDER_PROGRESS,
  SET_PREVIEWS,
  SET_PREVIEWS_LOADING,
  SET_FOLDER_METADATA_POLLING,
  SET_LOCAL_FOLDER_SELECTIONS,
  SET_INVALID_FOLDERS,
  // Dataset Processing
  SET_CREATION_STATUS,
  SET_CREATION_STATUS_POLLING,
  SET_VALIDATION_LOADING,
  // Resource Configuration
  SET_RESOURCE_CONFIGURATION,
  RESET_RESOURCE_CONFIGURATION,
  SET_SHOW_RESOURCE_CONFIGURATION_MODAL,
} from "./constants";
import {
  DataPreview,
  DatasetStatusEnum,
  DataSources,
  LastUsedData,
  StepTileProgressStatus,
  FilesUploadSize,
  ActivityLogShowModalType,
  RawCVATTask,
  RawCVATProject,
  UploadingFolder,
  FolderUploadStatus,
  MultiFolderUploadState,
  FolderMetadata,
  PreviewItem,
  CreationStatusResponse,
} from "types";
import {
  ResourceConfiguration,
  DEFAULT_RESOURCE_CONFIG,
} from "helpers/constants/resourceConfiguration";

// Clean separation: server state vs local UI selections

const INITIAL_STATE: CreateDatasetState = {
  connectStatus: StepTileProgressStatus.ACTIVE,
  annotationStatus: StepTileProgressStatus.DISABLED,
  uploadStatus: StepTileProgressStatus.DISABLED,
  indexingStatus: StepTileProgressStatus.DISABLED,
  s3FetchProgress: -1,
  previewData: [],
  previewDataSource: null,
  newDataset: null,
  activityLogs: [],
  imageErrorFileName: "",
  activityOffset: 0,
  isFatalError: false,
  filesUploadTransId: null,
  limitExceededDetails: null,
  areAnnotationsFetched: false,
  statusCallInProgress: false,
  isLoading: false,
  showDeleteDatasetModal: false,
  showActivityLogModal: { show: false, reason: "", title: "", severity: "" },
  datasetStatus: null,
  lastUsedData: { s3Url: "", folderName: "", annotationFiles: [] },
  uploadFinished: false,
  userInitiatedCreation: false,
  filesUploadSize: null,
  filesUploadProgress: null,

  cvatData: { tasks: [], projects: [] },
  isCvatLoading: false,

  // New Dataset Creation Workflow
  foldersMetadata: [],
  uploadingFolder: null,
  folderUploadStatus: FolderUploadStatus.IDLE,
  multiFolderUpload: null,
  previews: [],
  previewsLoading: false,

  // Frontend validation
  invalidFolders: [],
  folderMetadataPolling: false,
  localFolderSelections: [],

  // Validation loading state
  validationLoading: false,

  // Dataset Processing
  creationStatus: null,
  creationStatusPolling: false,

  // Resource Configuration
  resourceConfiguration: DEFAULT_RESOURCE_CONFIG,
  showResourceConfigurationModal: false,
};

const reducer = (state: CreateDatasetState = INITIAL_STATE, action: any) => {
  switch (action.type) {
    case RESET_STORE:
      return INITIAL_STATE;

    case SET_CONNECT_STATUS:
      return { ...state, connectStatus: action.data };

    case SET_S3_FETCH_PROGRESS:
      return { ...state, s3FetchProgress: action.data };

    case SET_PREVIEW_DATA:
      return { ...state, previewData: action.data };

    case SET_PREVIEW_DATA_SOURCE:
      return { ...state, previewDataSource: action.data };

    case SET_NEW_DATASET:
      return { ...state, newDataset: action.data };

    case SET_ACTIVITY_LOGS:
      return { ...state, activityLogs: action.data };

    case SET_ANNOTATION_STATUS:
      return { ...state, annotationStatus: action.data };

    case SET_UPLOAD_STATUS:
      return { ...state, uploadStatus: action.data };

    case SET_IMAGE_ERROR_FILE_NAME:
      return { ...state, imageErrorFileName: action.data };

    case SET_ACTIVITY_OFFSET:
      return { ...state, activityOffset: action.data };

    case SET_IS_FATAL_ERROR:
      return { ...state, isFatalError: action.data };

    case SET_FILES_UPLOAD_TRANS_ID:
      return { ...state, filesUploadTransId: action.data };

    case SET_INDEXING_STATUS:
      return { ...state, indexingStatus: action.data };

    case SET_LIMIT_EXCEEDED_DETAILS:
      return { ...state, limitExceededDetails: action.data };

    case SET_ARE_ANNOTATIONS_FETCHED:
      return { ...state, areAnnotationsFetched: action.data };

    case SET_STATUS_CALL_IN_PROGRESS:
      return { ...state, statusCallInProgress: action.data };

    case SET_IS_LOADING:
      return { ...state, isLoading: action.data };

    case SET_SHOW_DELETE_DATASET_MODAL:
      return { ...state, showDeleteDatasetModal: action.data };

    case SET_LAST_USED_DATA:
      return { ...state, lastUsedData: action.data };

    case SET_UPLOAD_FINISHED:
      return { ...state, uploadFinished: action.data };

    case SET_USER_INITIATED_CREATION:
      return { ...state, userInitiatedCreation: action.data };

    case SET_FILES_UPLOAD_SIZE:
      return { ...state, filesUploadSize: action.data };

    case SET_FILES_UPLOAD_PROGRESS:
      return { ...state, filesUploadProgress: action.data };

    case SET_SHOW_ACTIVITY_LOG_MODAL:
      return { ...state, showActivityLogModal: action.data };

    case SET_CVAT_DATA:
      return { ...state, cvatData: action.data };

    case SET_IS_CVAT_LOADING:
      return { ...state, isCvatLoading: action.data };

    case SET_FOLDER_UPLOAD_STATUS:
      return { ...state, folderUploadStatus: action.data };

    case SET_UPLOADING_FOLDER:
      return { ...state, uploadingFolder: action.data };

    case SET_MULTI_FOLDER_UPLOAD:
      return { ...state, multiFolderUpload: action.data };

    case UPDATE_MULTI_FOLDER_PROGRESS:
      if (!state.multiFolderUpload) return state;

      const { progress, transactionId } = action.data;
      const updatedFolders = state.multiFolderUpload.folders.map((folder) =>
        folder.transactionId === transactionId
          ? { ...folder, progress }
          : folder,
      );

      // Calculate overall progress
      const overallProgress = Math.round(
        updatedFolders.reduce((sum, folder) => sum + folder.progress, 0) /
          updatedFolders.length,
      );

      return {
        ...state,
        multiFolderUpload: {
          ...state.multiFolderUpload,
          folders: updatedFolders,
          overallProgress,
        },
      };

    case SET_FOLDERS_METADATA:
      return { ...state, foldersMetadata: action.data };

    case SET_PREVIEWS:
      return { ...state, previews: action.data };

    case SET_PREVIEWS_LOADING:
      return { ...state, previewsLoading: action.data };

    case SET_FOLDER_METADATA_POLLING:
      return { ...state, folderMetadataPolling: action.data };

    case SET_LOCAL_FOLDER_SELECTIONS:
      return { ...state, localFolderSelections: action.data };

    case SET_INVALID_FOLDERS:
      return { ...state, invalidFolders: action.data };

    case SET_CREATION_STATUS:
      return { ...state, creationStatus: action.data };

    case SET_CREATION_STATUS_POLLING:
      return { ...state, creationStatusPolling: action.data };

    case SET_VALIDATION_LOADING:
      return state.validationLoading === action.data
        ? state
        : { ...state, validationLoading: action.data };

    case SET_RESOURCE_CONFIGURATION:
      return { ...state, resourceConfiguration: action.data };

    case RESET_RESOURCE_CONFIGURATION:
      return {
        ...state,
        resourceConfiguration: DEFAULT_RESOURCE_CONFIG,
      };

    case SET_SHOW_RESOURCE_CONFIGURATION_MODAL:
      return { ...state, showResourceConfigurationModal: action.data };

    default:
      return state;
  }
};

export default reducer;

interface CreateDatasetState {
  connectStatus: StepTileProgressStatus;
  annotationStatus: StepTileProgressStatus;
  uploadStatus: StepTileProgressStatus;
  indexingStatus: StepTileProgressStatus;
  s3FetchProgress: number;
  filesUploadTransId: string | null;
  previewData: DataPreview[];
  previewDataSource: DataSources | null;
  newDataset: DatasetInitialized | null;
  activityLogs: any[];
  imageErrorFileName: string;
  activityOffset: number;
  isFatalError: boolean;
  limitExceededDetails: LimitExceeded | null;
  areAnnotationsFetched: boolean;
  statusCallInProgress: boolean;
  isLoading: boolean;
  showDeleteDatasetModal: Boolean;
  showActivityLogModal: ActivityLogShowModalType;
  datasetStatus: DatasetStatusEnum | null;
  lastUsedData: LastUsedData;
  uploadFinished: boolean;
  userInitiatedCreation: boolean;
  filesUploadSize: FilesUploadSize | null;
  filesUploadProgress: number | null;
  cvatData: { tasks: RawCVATTask[]; projects: RawCVATProject[] };
  isCvatLoading: boolean;

  // New Dataset Creation Workflow
  foldersMetadata: FolderMetadata[];
  folderUploadStatus: FolderUploadStatus;
  uploadingFolder: UploadingFolder | null;
  multiFolderUpload: MultiFolderUploadState | null;
  previews: PreviewItem[];
  previewsLoading: boolean;
  folderMetadataPolling: boolean;

  // Frontend validation - invalid folders that won't be sent to backend
  invalidFolders: FolderMetadata[];

  // Client-side selections (UI state only)
  localFolderSelections: string[]; // folder IDs

  // Validation loading state
  validationLoading: boolean;

  // Dataset Processing
  creationStatus: CreationStatusResponse | null;
  creationStatusPolling: boolean;

  // Resource Configuration
  resourceConfiguration: ResourceConfiguration;
  showResourceConfigurationModal: boolean;
}
